import React, { useState, useEffect } from "react";
import { X, Search, Check, Loader2 } from "lucide-react";
import { displayNameFromOrgMember, UserAvatar } from "../DesignShared";
import { updateDesignMembers } from "../../../../api/design/service";
import { useAlert } from "components/UI_components/Alert";

const DesignMemberSelector = ({
    show,
    onClose,
    designId,
    orgMembers = [],
    orgMembersLoading = false,
    currentDesignMembers = [],
    onUpdate,
}) => {
    const [selectedIds, setSelectedIds] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [saving, setSaving] = useState(false);
    const { addAlert } = useAlert();

    useEffect(() => {
        if (show) {
            // Initialize selected ids from current design members
            const initialIds = currentDesignMembers.map((m) => m.member_id || m.id);
            setSelectedIds(initialIds);
            setSearchTerm("");
        }
    }, [show, currentDesignMembers]);

    if (!show) return null;

    const filteredMembers = orgMembers.filter((m) => {
        const name = displayNameFromOrgMember(m).toLowerCase();
        return name.includes(searchTerm.toLowerCase());
    });

    const toggleMember = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            await updateDesignMembers(designId, selectedIds);
            addAlert("Design members updated successfully", "success");
            if (onUpdate) await onUpdate();
            onClose();
        } catch (err) {
            console.error("Failed to update design members:", err);
            addAlert(err?.message || "Failed to update design members", "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-4">
            <div className="bg-white rounded-xl w-full max-w-md flex flex-col max-h-[80vh] shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="p-4 border-b flex items-center justify-between bg-gray-50/50">
                    <div>
                        <h3 className="font-bold text-gray-900">Manage Design Members</h3>
                        <p className="text-xs text-gray-500">{selectedIds.length} members selected</p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-1.5 hover:bg-gray-200 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Search */}
                <div className="p-3 border-b">
                    <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search members..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-9 pr-4 py-2 bg-gray-100 border-none rounded-lg text-sm focus:ring-2 focus:ring-sm-primary/20 transition-all outline-none"
                        />
                    </div>
                </div>

                {/* Member List */}
                <div className="flex-1 overflow-y-auto p-2 min-h-[300px]" style={{ scrollbarWidth: "none" }}>
                    {orgMembersLoading ? (
                        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                            <Loader2 className="w-8 h-8 animate-spin mb-2" />
                            <span className="text-xs font-medium">Loading organization members...</span>
                        </div>
                    ) : filteredMembers.length === 0 ? (
                        <div className="text-center py-12 text-gray-400 text-xs italic">
                            No members found matching "{searchTerm}"
                        </div>
                    ) : (
                        <div className="space-y-1">
                            {filteredMembers.map((m) => {
                                const isSelected = selectedIds.includes(m.id);
                                return (
                                    <div
                                        key={m.id}
                                        onClick={() => toggleMember(m.id)}
                                        className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-all ${isSelected
                                            ? "bg-sm-primary/5 ring-1 ring-sm-primary/10"
                                            : "hover:bg-gray-50"
                                            }`}
                                    >
                                        <div className="relative">
                                            <UserAvatar member={m} size="w-9 h-9" />
                                            {isSelected && (
                                                <div className="absolute -right-1 -bottom-1 bg-sm-primary text-white rounded-full p-0.5 shadow-sm">
                                                    <Check className="w-2.5 h-2.5" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-gray-900 truncate">
                                                {displayNameFromOrgMember(m)}
                                            </p>
                                            <p className="text-[10px] text-gray-400 truncate uppercase tracking-wider font-medium">
                                                {m.joined_as || "Member"}
                                            </p>
                                        </div>
                                        <div
                                            className={`w-5 h-5 rounded-md border-2 transition-all flex items-center justify-center ${isSelected
                                                ? "bg-sm-primary border-sm-primary"
                                                : "border-gray-200"
                                                }`}
                                        >
                                            {isSelected && <Check className="w-3.5 h-3.5 text-white" />}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t bg-gray-50/50 flex items-center justify-between gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving || orgMembersLoading}
                        className="flex-1 bg-sm-primary text-white py-2 px-4 rounded-lg text-sm font-bold shadow-lg shadow-sm-primary/20 hover:bg-sm-primary/90 disabled:opacity-50 disabled:shadow-none transition-all flex items-center justify-center gap-2"
                    >
                        {saving ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Updating...
                            </>
                        ) : (
                            "Save Changes"
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DesignMemberSelector;
